import { RouterProvider, createRouter } from '@tanstack/react-router';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BlockElementContext } from '#src/hooks/use-block-element';
import { initTranslations } from '#src/utils/i18n/translations-init';
import { worker } from './api';
import { routeTree } from './routeTree.gen';
import { mockDrupalSettings } from './utils/drupal-settings/mock-drupal-settings';

// Only import parent CSS in development
if (import.meta.env.DEV) {
  import('../parent-project/css/index.css')
    .then(() => import('./styles/index.scss'))
    .catch((error) => {
      console.error('Error dev CSS:', error);
    });

  mockDrupalSettings();
} else {
  import('./styles/index.scss').catch((error) => {
    console.error('Error loading styles:', error);
  });
}

initTranslations();

// Create a new router instance
export const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function initApp() {
  // Check if we're in a Drupal environment (multiple blocks)
  const drupalBlocks = document.querySelectorAll('.gfx-dashboards-block');

  if (drupalBlocks.length > 0) {
    // Initialize React app on each Drupal block instance
    drupalBlocks.forEach((blockElement) => {
      if (!blockElement.innerHTML.trim()) {
        const root = ReactDOM.createRoot(blockElement as HTMLElement);
        root.render(
          <React.StrictMode>
            <BlockElementContext.Provider value={blockElement.id}>
              <RouterProvider router={router} />
            </BlockElementContext.Provider>
          </React.StrictMode>
        );
      }
    });
  } else {
    // Fallback to single element with ID 'root' for development/standalone
    const rootElement = document.getElementById('root');
    if (rootElement && !rootElement.innerHTML) {
      const root = ReactDOM.createRoot(rootElement);
      root.render(
        <React.StrictMode>
          <BlockElementContext.Provider value={rootElement.id as string}>
            <RouterProvider router={router} />
          </BlockElementContext.Provider>
        </React.StrictMode>
      );
    }
  }
}

const shouldRunMocks = process.env.ENABLE_API_MOCKS === 'true';
if (shouldRunMocks) {
  worker
    .start({
      serviceWorker: {
        url: '/gfx-mocks-service-worker.js',
      },
    })
    .then(() => {
      initApp();
    })
    .catch((error) => {
      console.error('Error starting the worker:', error);
    });
} else {
  worker.stop();
  initApp();
}
